import { memo, useEffect, useState } from 'react';

import { ChartWithBarsWrapper } from 'PDF/components/Charts/ChartWithBars';
import { ChartWithStackedBars } from 'PDF/components/Charts/ChartWithStackedBars';
import { DonutWithTextWrapper } from 'PDF/components/Charts/DonutWithText';
import { IImageChart } from 'PDF/components/Charts/types';
import { PDF_SECTIONS } from 'PDF/constants/sections';
import { prepareSectionId } from 'PDF/helpers/pdfMapping';
import { useGenerateImageFromCharts } from 'PDF/hooks/useGenerateImageFromCharts';
import { IPdfPageProps } from 'PDF/types/pdf';
import { useBusinessToolStore } from 'store/useBusinessToolStore';
import { getBusinessPlanChapters, getBusinessPlanType } from 'store/useBusinessToolStore/selectors';

import { useExamplePreview } from 'hooks/useExamplePreview';

import {
  getB2B,
  getB2C,
  getCompetitorsComparison,
  getMagazineItems,
  getMarketTrends,
  getPriceOverview,
  getProductComparison,
  getStrengthAndWeakness,
  useGetConsumptionForecast,
  useGetStructureDetails,
} from './mapping';

export const MarketAnalysis = memo(({ saveSectionProps, sectionPicture }: IPdfPageProps) => {
  const [consumptionForecastImages, setConsumptionForecastImages] = useState<IImageChart[]>([]);
  const [structureDetailsImages, setStructureDetailsImages] = useState<IImageChart[]>([]);
  const businessPlanChapters = useBusinessToolStore(getBusinessPlanChapters);
  const businessPlanType = useBusinessToolStore(getBusinessPlanType);
  const { isExamplePage } = useExamplePreview();
  const magazineItems = getMagazineItems(businessPlanChapters, businessPlanType, isExamplePage);
  const marketTrends = getMarketTrends(businessPlanChapters);
  const consumptionForecastArray = useGetConsumptionForecast(businessPlanChapters, isExamplePage);
  const structuteDetailsArray = useGetStructureDetails(businessPlanChapters, isExamplePage);
  const priceOverviewChart = getPriceOverview(businessPlanChapters, isExamplePage);
  const competitorsComparison = getCompetitorsComparison(businessPlanChapters, isExamplePage);
  const strengthAndWeakness = getStrengthAndWeakness(businessPlanChapters, isExamplePage);
  const productComparisonArray = getProductComparison(businessPlanChapters, isExamplePage);
  const B2C = getB2C(businessPlanChapters, isExamplePage);
  const B2B = getB2B(businessPlanChapters, isExamplePage);

  const [priceOverviewPortal, priceOverviewChartImageSrc] = useGenerateImageFromCharts(
    // @ts-expect-error no types
    ChartWithStackedBars,
    { isSmall: true, info: priceOverviewChart, withBarInsideLabels: true, withBarTopLabels: false },
  );

  const data = {
    marketTrends,
    consumptionForecastImages,
    structureDetailsImages,
    priceOverviewChartImage: {
      title: priceOverviewChart.title,
      src: priceOverviewChartImageSrc,
      subTitle: priceOverviewChart.subtitle,
    },
    competitorsComparison,
    strengthAndWeakness,
    productComparisonArray,
    B2C,
    B2B,
  };

  const updateConsumptionForecastImages = (title: string, src: string) => {
    setConsumptionForecastImages((prevState) => [...prevState, { title, src }]);
  };

  const updateStructureDetailsImages = (title: string, src: string) => {
    setStructureDetailsImages((prevState) => [...prevState, { title, src }]);
  };

  useEffect(() => {
    if (
      consumptionForecastImages.length === consumptionForecastArray.length &&
      structureDetailsImages.length === structuteDetailsArray.length &&
      priceOverviewChartImageSrc &&
      sectionPicture
    ) {
      saveSectionProps(prepareSectionId(businessPlanType, PDF_SECTIONS.MARKET_ANALYSIS), {
        magazineItems,
        data,
      });
    }
  }, [
    consumptionForecastImages,
    structureDetailsImages,
    consumptionForecastArray,
    structuteDetailsArray,
    priceOverviewChartImageSrc,
    sectionPicture,
  ]);

  return (
    <>
      {consumptionForecastArray.map((item, index) => (
        <ChartWithBarsWrapper
          key={index}
          aspectRatio={2.5}
          info={item}
          updateState={updateConsumptionForecastImages}
          withDataLabeInsideBar
        />
      ))}
      {structuteDetailsArray.map((item, index) => (
        <DonutWithTextWrapper
          key={index}
          aspectRatio={3}
          info={item}
          updateState={updateStructureDetailsImages}
          labelTextWidthDivider={12}
        />
      ))}
      {priceOverviewPortal}
    </>
  );
});
